import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { GOALS, EXPERIENCE_LEVELS, LOCATION_PREFERENCES } from "../../lib/constants";
import { ArrowRight, ArrowLeft, CheckCircle, Check } from "@phosphor-icons/react";
import { useSiteContent } from "../../contexts/SiteContentContext";
import { DEFAULT_SITE_CONTENT } from "../../lib/siteContentDefaults";

const initialForm = { goals: [], experience_level: "", location_preference: "", name: "", email: "", phone: "" };
const STEP_LABELS = ["Goals", "Experience & Location", "Your Info"];

function OptionTile({ selected, onClick, testId, icon, label, className = "" }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`relative flex items-center gap-4 text-left px-6 py-5 rounded-2xl border-2 transition-all duration-200 ${
        selected
          ? "border-[#D4AF37] bg-[#D4AF37]/10 shadow-[0_0_24px_rgba(212,175,55,0.15)]"
          : "border-[#27272A] bg-[#0A0A0A]/40 hover:border-white/30"
      } ${className}`}
    >
      {icon && <span className="text-3xl leading-none shrink-0">{icon}</span>}
      <span className={`font-semibold text-base ${selected ? "text-white" : "text-[#D4D4D8]"}`}>{label}</span>
      {selected && (
        <span className="absolute top-3 right-3 flex items-center justify-center w-6 h-6 rounded-full bg-[#D4AF37] text-black shrink-0">
          <Check size={14} weight="bold" />
        </span>
      )}
    </button>
  );
}

export default function Quiz() {
  const c = { ...DEFAULT_SITE_CONTENT, ...useSiteContent() };
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const canNext = step === 1 ? form.goals.length > 0 : step === 2 ? !!form.experience_level && !!form.location_preference : true;

  const toggleGoal = (value) => {
    setForm((f) => ({
      ...f,
      goals: f.goals.includes(value) ? f.goals.filter((g) => g !== value) : [...f.goals, value],
    }));
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/leads", { ...form, type: "quiz" });
      setDone(true);
      toast.success("Got it! Coach Conde will reach out within 24 hours.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <section id="quiz" className="py-24 px-6 bg-[#0A0A0A]">
        <div data-testid="quiz-success-state" className="max-w-xl mx-auto text-center border border-[#27272A] rounded-2xl p-12 bg-[#141414]">
          <CheckCircle size={48} weight="fill" className="text-[#D4AF37] mx-auto mb-4" />
          <h3 data-testid="quiz-success-title" className="font-heading text-3xl text-white tracking-tight mb-2">{c.quiz_success_title}</h3>
          <p data-testid="quiz-success-body" className="text-[#A1A1AA]">{c.quiz_success_body}</p>
        </div>
      </section>
    );
  }

  return (
    <section id="quiz" className="py-24 px-6 bg-[#0A0A0A]">
      <div className="max-w-5xl mx-auto">
        <p data-testid="quiz-eyebrow" className="font-heading uppercase tracking-[0.2em] text-xs text-[#D4AF37] mb-2">{c.quiz_eyebrow}</p>
        <h2 data-testid="quiz-title" className="font-heading text-3xl sm:text-4xl md:text-5xl text-white tracking-tight mb-10">
          {c.quiz_title}
        </h2>

        <div className="flex items-center justify-between mb-3">
          <p data-testid="quiz-step-counter" className="text-sm text-[#A1A1AA]">
            Step <span className="text-[#D4AF37] font-semibold">{step}</span> of 3: {STEP_LABELS[step - 1]}
          </p>
        </div>
        <div className="flex gap-2 mb-10">
          {[1, 2, 3].map((n) => (
            <div key={n} className={`h-2.5 flex-1 rounded-full transition-colors ${n <= step ? "bg-[#D4AF37]" : "bg-[#27272A]"}`} />
          ))}
        </div>

        <div className="bg-[#141414] border border-[#27272A] rounded-3xl p-8 sm:p-12 lg:p-14">
          {step === 1 && (
            <div data-testid="quiz-step-1">
              <p data-testid="quiz-step1-question" className="text-white font-heading text-3xl sm:text-4xl tracking-tight mb-2">{c.quiz_step1_question}</p>
              <p data-testid="quiz-step1-subtitle" className="text-sm text-[#71717A] mb-8">{c.quiz_step1_subtitle}</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {GOALS.map((g) => (
                  <OptionTile
                    key={g.value}
                    testId={`quiz-goal-${g.value}`}
                    selected={form.goals.includes(g.value)}
                    onClick={() => toggleGoal(g.value)}
                    icon={g.icon}
                    label={g.label}
                  />
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="quiz-step-2" className="space-y-10">
              <div>
                <p data-testid="quiz-step2-question1" className="text-white font-heading text-3xl sm:text-4xl tracking-tight mb-6">{c.quiz_step2_question1}</p>
                <div className="grid grid-cols-3 gap-4">
                  {EXPERIENCE_LEVELS.map((l) => (
                    <OptionTile
                      key={l.value}
                      testId={`quiz-experience-${l.value}`}
                      selected={form.experience_level === l.value}
                      onClick={() => setForm((f) => ({ ...f, experience_level: l.value }))}
                      label={l.label}
                      className="justify-center"
                    />
                  ))}
                </div>
              </div>
              <div>
                <p data-testid="quiz-step2-question2" className="text-white font-heading text-3xl sm:text-4xl tracking-tight mb-6">{c.quiz_step2_question2}</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  {LOCATION_PREFERENCES.map((l) => (
                    <OptionTile
                      key={l.value}
                      testId={`quiz-location-${l.value}`}
                      selected={form.location_preference === l.value}
                      onClick={() => setForm((f) => ({ ...f, location_preference: l.value }))}
                      label={l.label}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="quiz-step-3">
              <p data-testid="quiz-step3-question" className="text-white font-heading text-3xl sm:text-4xl tracking-tight mb-6">{c.quiz_step3_question}</p>
              <div className="grid gap-4">
                <input
                  data-testid="quiz-name-input"
                  placeholder="Full Name"
                  required
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="bg-[#0A0A0A] border border-[#27272A] rounded-xl px-5 py-4 text-white placeholder:text-[#71717A] focus:outline-none focus:border-[#D4AF37]"
                />
                <input
                  data-testid="quiz-email-input"
                  type="email"
                  placeholder="Email"
                  required
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  className="bg-[#0A0A0A] border border-[#27272A] rounded-xl px-5 py-4 text-white placeholder:text-[#71717A] focus:outline-none focus:border-[#D4AF37]"
                />
                <input
                  data-testid="quiz-phone-input"
                  placeholder="Phone"
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  className="bg-[#0A0A0A] border border-[#27272A] rounded-xl px-5 py-4 text-white placeholder:text-[#71717A] focus:outline-none focus:border-[#D4AF37]"
                />
              </div>
            </div>
          )}

          <div className="flex justify-end items-center gap-4 mt-12">
            <button
              data-testid="quiz-back-button"
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              className={`flex items-center gap-2 text-base border border-white/20 text-white rounded-full px-7 py-3.5 hover:bg-white/10 transition-colors ${
                step === 1 ? "opacity-0 pointer-events-none" : ""
              }`}
            >
              <ArrowLeft size={18} /> Back
            </button>
            {step < 3 ? (
              <button
                data-testid="quiz-next-button"
                disabled={!canNext}
                onClick={() => setStep((s) => s + 1)}
                className="flex items-center gap-2 bg-[#D4AF37] disabled:opacity-30 text-black font-semibold text-base px-8 py-3.5 rounded-full hover:bg-white transition-colors"
              >
                Next <ArrowRight size={18} />
              </button>
            ) : (
              <button
                data-testid="quiz-submit-button"
                disabled={!form.name || !form.email || submitting}
                onClick={submit}
                className="flex items-center gap-2 bg-[#D4AF37] disabled:opacity-30 text-black font-semibold text-base px-8 py-3.5 rounded-full hover:bg-white transition-colors"
              >
                {submitting ? "Sending..." : "Get My Plan"} <ArrowRight size={18} />
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
